import React, { useMemo } from 'react';
import _ from 'lodash';
import Grid from '@mui/material/Grid';

import { Typography, Card, CardHeader, CardContent } from '@occc/ui-design-system';
import { ConstraintFragment } from '@occc/graphql';

import InAudience from './InAudience';
import { LocationInState } from './LocationInState';
import { DaysOfWeek } from './DaysOfWeek';
import { TimeOfDay } from './TimeOfDay';
import { RequiresVoucherForDiscount } from './RequiresVoucherForDiscount';
import { RequiresVoucher } from './RequiresVoucher';
import { StoreLocation } from './StoreLocation';
import { RequiresActivation } from './RequiresActivation';
import {
  isDaysOfWeekArray,
  isLocationInState,
  isRequiresActivation,
  isRequiresVoucher,
  isRequiresVoucherForDiscount,
  isStoreLocation,
  isTimeOfDayArray,
} from './utils';

interface IConstraintsProps {
  constraints: ConstraintFragment[];
}

export const Constraints: React.FC<IConstraintsProps> = ({ constraints }) => {
  const {
    ConstraintLocationInState: locationInState,
    ConstraintRequiresActivation: requiresActivation,
    ConstraintStoreLocation: storeLocation,
    ConstraintRequiresVoucher: requiresVoucher,
    ConstraintRequiresVoucherAvailableForDiscount: requiresVoucherForDiscount,
    ConstraintTimeOfDay: timeOfDay,
    ConstraintDaysOfWeek: daysOfWeek,
    ConstraintInAudience: inAudience,
  } = useMemo(() => _.groupBy(constraints, '__typename'), [constraints]);

  const inAudienceConstraintsIds = useMemo(() => _.map(inAudience, 'audienceId'), [inAudience]);

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant='h6' component='h4' gutterBottom>
            Constraints
          </Typography>
        }
      />
      <CardContent>
        <Grid container={true} spacing={2}>
          {locationInState?.length && isLocationInState(locationInState[0]) ? (
            <Grid item={true} md={3} sm={6}>
              <LocationInState states={locationInState[0].states} />
            </Grid>
          ) : null}
          {storeLocation?.length && isStoreLocation(storeLocation[0]) ? (
            <Grid item={true} md={3} sm={6}>
              <StoreLocation
                evaluationMethod={storeLocation[0].evaluationMethod}
                locations={storeLocation[0].locations}
              />
            </Grid>
          ) : null}
          {requiresActivation?.length && isRequiresActivation(requiresActivation[0]) ? (
            <Grid item={true} md={3} sm={6}>
              <RequiresActivation
                mustFulfillWithinDays={requiresActivation[0].mustFulfillWithinDays}
              />
            </Grid>
          ) : null}
          {requiresVoucher?.length && isRequiresVoucher(requiresVoucher[0]) ? (
            <Grid item={true} md={3} sm={6}>
              <RequiresVoucher />
            </Grid>
          ) : null}
          {requiresVoucherForDiscount?.length &&
          isRequiresVoucherForDiscount(requiresVoucherForDiscount[0]) ? (
            <Grid item={true} md={3} sm={6}>
              <RequiresVoucherForDiscount />
            </Grid>
          ) : null}
          {isTimeOfDayArray(timeOfDay) && (
            <Grid item={true} md={3} sm={6}>
              <TimeOfDay timeOfDayConstraints={timeOfDay} />
            </Grid>
          )}
          {isDaysOfWeekArray(daysOfWeek) && (
            <Grid item={true} md={3} sm={6}>
              <DaysOfWeek daysOfWeekConstraints={daysOfWeek} />
            </Grid>
          )}
          {inAudienceConstraintsIds.length ? (
            <Grid item={true} xs={12}>
              <InAudience audienceIds={inAudienceConstraintsIds} />
            </Grid>
          ) : null}
        </Grid>
      </CardContent>
    </Card>
  );
};
